import { Input } from 'components/atoms';
import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { IArtistMutationForm } from 'services/artists/types';
import { STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import {
  conditionalField,
  externalIdsValidation,
  requiredEnumSelect,
} from 'utils/global/validation.utils';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { array, bool, number, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import ArtistBand, { artistsInBandSchema } from './tabs/ArtistBand';
import ArtistExternal from './tabs/ArtistExternal';
import ArtistRemarks from './tabs/ArtistRemarks';

export enum ArtistTabs {
  external,
  remarks,
  bio,
  gallery,
  actions,
}

export enum BandTabs {
  band,
  external,
  remarks,
}

export const artistInitialValues: IArtistMutationForm = {
  id: '',
  email: '',
  firstName: '',
  isBand: false,
  isBandOption: 0,
  labelId: '',
  labelName: '',
  lastName: '',
  nickname: '',
  phoneNumber: {
    number: '',
    prefix: '',
  },
  remarks: '',
  statusId: STATUS.draft,
  suffix: '',

  profilePictureLink: '',
  profilePicturePreviewLink: '',
  artistsInBand: [],

  prefix: '',

  externalProfiles: [],
};

export const artistPhoneNumberValidator = object({
  prefix: string()
    .nullable()
    .test(
      'prefix-required-if-number',
      i18next.t('formik-error.required'),
      function (value) {
        const { number } = this.parent;
        const hasNumber = typeof number === 'string' && number.trim() !== '';
        const hasPrefix = typeof value === 'string' && value.trim() !== '';
        if (hasNumber && !hasPrefix) {
          return this.createError({
            path: this.path,
            message: i18next.t('formik-error.required'),
          });
        }
        return true;
      }
    ),
  number: string()
    .nullable()
    .test(
      'number-required-if-prefix',
      i18next.t('formik-error.required'),
      function (value) {
        const { prefix } = this.parent;
        const hasPrefix = typeof prefix === 'string' && prefix.trim() !== '';
        const hasNumber = typeof value === 'string' && value.trim() !== '';
        if (hasPrefix && !hasNumber) {
          return this.createError({
            path: this.path,
            message: i18next.t('formik-error.required'),
          });
        }
        return true;
      }
    ),
});

export const artistValidator = object().shape({
  isBandOption: number().required(() => i18next.t('formik-error.required')),
  isBand: bool().required(() => i18next.t('formik-error.required')),
  statusId: requiredEnumSelect(STATUS),
  firstName: string().required(() => i18next.t('formik-error.required')),
  lastName: string().required(() => i18next.t('formik-error.required')),
  nickname: string().required(() => i18next.t('formik-error.required')),
  email: string()
    .email(() => i18next.t('formik-error.invalid-email'))
    .optional()
    .nullable(),
  phoneNumber: artistPhoneNumberValidator,
  prefix: string().nullable(),
  suffix: string().nullable(),
  labelId: string().nullable(),
  labelName: string(),
  remarks: string().nullable(),
  externalProfiles: array().of(externalIdsValidation),
  artistsInBand: conditionalField(
    conditionalField(
      array()
        .of(artistsInBandSchema)
        .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
        .required(() => i18next.t('formik-error.required')),
      'isBandOption',
      [0],
      array().of(artistsInBandSchema)
    ),
    'statusId'
  ),
});

export interface IArtistForm
  extends FormikConfig<IArtistMutationForm>,
    IFormProps,
    II18nextPrefix {}

const ArtistForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  initialValues = artistInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  status,
  ...props
}: IArtistForm) => {
  const { t } = useTranslation();
  const { getStatusOptions } = useEnumToSelectOptions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <Formik
      {...props}
      validationSchema={artistValidator}
      validateOnChange
      validateOnBlur
      validateOnMount
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        props.onSubmit?.(values, helpers);
      }}>
      {({
        setFieldValue,
        values,
        status,
        errors,
      }: IFormikProps<IArtistMutationForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'artist'}
              topbarContent={
                <div className=' w-full flex justify-end'>
                  {status?.displayMode ? null : (
                    <div className='w-48'>
                      <FormInput.Text
                        name='nickname'
                        label={
                          !values.isBand
                            ? t('input.label.nickname')
                            : t('input.label.bandName')
                        }
                        autoFocus
                      />
                    </div>
                  )}
                  <div className='flex items-center gap-2 w-full justify-end'>
                    {status?.displayMode ? (
                      initialValues?.statusId
                    ) : (
                      <div className='w-44'>
                        <FormInput.Select
                          name='statusId'
                          label={t('input.label.status')}
                          options={getStatusOptions(
                            STATUS,
                            initialValues?.statusId,
                            'enum.STATUS'
                          )}
                        />
                      </div>
                    )}
                    {topbarContent}
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Image
                  fullResolutionUrl={values.profilePictureLink}
                  previewUrl={values.profilePicturePreviewLink}
                  editable={!status?.displayMode}
                  onChange={file => setFieldValue('file', file)}
                  onDelete={
                    initialValues.profilePictureLink
                      ? action => {
                          setFieldValue('profilePictureLink', '');
                          setFieldValue('profilePicturePreviewLink', '');
                          action.close();
                        }
                      : undefined
                  }
                />
                <InfoBox.Items columns={2}>
                  <Input.ComplexWrapper sizes={[30, 70]}>
                    <FormInput.Text
                      name='prefix'
                      label={t('input.label.prefix')}
                    />
                    <FormInput.Text
                      name='firstName'
                      label={
                        !values.isBand
                          ? t('input.label.firstName')
                          : t('input.label.supervisorFirstName')
                      }
                    />
                  </Input.ComplexWrapper>
                  <Input.ComplexWrapper sizes={[70, 30]}>
                    <FormInput.Text
                      name='lastName'
                      label={
                        !values.isBand
                          ? t('input.label.lastName')
                          : t('input.label.supervisorLastName')
                      }
                    />
                    <FormInput.Text
                      name='suffix'
                      label={t('input.label.suffix')}
                    />
                  </Input.ComplexWrapper>
                  <FormInput.Select
                    name='isBand'
                    label={t('input.label.type')}
                    onChange={option => {
                      setFieldValue('isBandOption', option.value ? 1 : 0);
                      if (!option.value) {
                        setFieldValue('artistsInBand', []);
                        getLastPathname(pathname) === BandTabs[BandTabs.band] &&
                          navigate(BandTabs[BandTabs.external]);
                      }
                    }}
                    options={[
                      { label: t('artist.band'), value: true },
                      { label: t('artist.solo'), value: false },
                    ]}
                  />
                  <FormInput.Text name='email' label='Email' />
                  <Input.ComplexWrapper sizes={[40, 60]}>
                    <FormInput.Select
                      name='phoneNumber.prefix'
                      label={t('input.label.phoneNumberCodeShort')}
                      placeholder=''
                      isCreatable
                      onChange={option =>
                        setFieldValue('phoneNumberPrefix', option.value)
                      }
                    />
                    <FormInput.Text
                      name='phoneNumber.number'
                      label={t('input.label.phoneNumber')}
                      type='number'
                    />
                  </Input.ComplexWrapper>
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  ...(values.isBand
                    ? [
                        {
                          value: BandTabs.band,
                          name: 'band',
                          error: errors.artistsInBand,
                        },
                      ]
                    : []),
                  {
                    value: values.isBand
                      ? BandTabs.external
                      : ArtistTabs.external,
                    name: 'external',
                    error: errors.externalProfiles,
                  },
                  {
                    value: values.isBand
                      ? BandTabs.remarks
                      : ArtistTabs.remarks,
                    name: 'remarks',
                    error: errors.remarks,
                  },
                  {
                    value: ArtistTabs.bio,
                    name: 'bio',
                  },
                  {
                    value: ArtistTabs.actions,
                    name: 'actions',
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ArtistForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'external', element: <ArtistExternal /> },
    { path: 'remarks', element: <ArtistRemarks /> },
    { path: 'band', element: <ArtistBand /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default ArtistForm;
